import { graphql } from 'react-relay';
import { useEffect } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import type { AutomatedMeetingNotesPagePropertiesResult } from '../__types__/AutomatedMeetingNotesPagePropertiesResult.ts';

import type {
	useAutomatedMeetingNotesPagePropertiesQuery as useAutomatedMeetingNotesPagePropertiesQuery$data,
	useAutomatedMeetingNotesPagePropertiesQueryVariables as useAutomatedMeetingNotesPagePropertiesQuery$variables,
} from './__types__/useAutomatedMeetingNotesPagePropertiesQuery.ts';

const createAriId = (cloudId: string, pageId: string) =>
	`ari:cloud:confluence:${cloudId}:page/${pageId}`;
const AutomatedMeetingNotesPagePropertiesQuery = graphql`
	query useAutomatedMeetingNotesPagePropertiesQuery($pageAri: ID!) {
		page(id: $pageAri) {
			properties(keys: ["isAutomatedMeetingNotes", "meetingRecurrenceAri"]) {
				nodes {
					key
					value
				}
			}
			subType
			id
		}
	}
`;

const IsAutomatedMeetingNotesPropertyKey = 'isAutomatedMeetingNotes';
const MeetingRecurrenceAriPropertyKey = 'meetingRecurrenceAri';

export const useAutomatedMeetingNotesPageProperties = (
	pageId: string,
): AutomatedMeetingNotesPagePropertiesResult => {
	const { cloudId } = useSessionData();
	const { data, loading, error } = useQueryInterop<
		useAutomatedMeetingNotesPagePropertiesQuery$data,
		useAutomatedMeetingNotesPagePropertiesQuery$variables
	>(AutomatedMeetingNotesPagePropertiesQuery, {
		variables: {
			pageAri: createAriId(cloudId, pageId),
		},
	});

	useEffect(() => {
		if (error) {
			if (!isUnauthorizedError(error)) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.INTEGRATIONS,
				});
			}
			markErrorAsHandled(error);
		}
	}, [error]);

	return {
		isLoading: loading,
		...extractAmnPageProperties(data),
	};
};

export const extractAmnPageProperties = (
	data: useAutomatedMeetingNotesPagePropertiesQuery$data | undefined,
) => {
	const pageProperties = data?.page?.properties?.nodes;
	const amnProperty = pageProperties?.find(
		(property) => property?.key === IsAutomatedMeetingNotesPropertyKey,
	);
	const recurrenceProperty = pageProperties?.find(
		(property) => property?.key === MeetingRecurrenceAriPropertyKey,
	);

	const isAMN = amnProperty?.value === 'true';
	const isRecurringParent = recurrenceProperty != undefined;
	const isLiveAMNPage = data?.page?.subType?.toLowerCase() === 'live' && isAMN;

	return {
		isAMN,
		isRecurringParent,
		isLiveAMNPage,
	};
};
